import {
  Button,
  Checkbox,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Textarea,
  Typography
} from "@material-tailwind/react";
import Dict from "../../helper/dict";
import React, {useCallback, useEffect, useState} from "react";
import "../../style/datePicker.css"
import {getBankAccounts} from "../../services/bank_account/bank_account";
import persianNumberFormat from "../../helper/PersianNumberFormat";
import SelectInput from "../system/select";
import FileCell from "../system/fileViewer";
import {getWithdrawalAvailableSources, submitWalletWithdrawal} from "../../services/wallet/wallet";
import {toast} from "react-toastify";
import PersianDateTime from "../system/persianDateTime";
import {getWithdrawalErrorMessage, WITHDRAWAL_MESSAGES} from "../../helper/withdrawalMessages";

const MIN_WITHDRAWAL_AMOUNT = 10000000;

const getErrorMessage = (result) => {
  return result?.data?.error || result?.data?.message || "";
};

export function UserWithdrawalForm({withdrawal, open, setOpen, setLastDataRefresh, setGridSelectedRow}) {
  const handleOpen = () => setOpen(!open);

  const [id, setId] = useState(null)
  const [value, setValue] = useState(0)
  const [accountId, setAccountId] = useState(null)
  const [description, setDescription] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [initialStatus, setInitialStatus] = useState("");
  const [availableSources, setAvailableSources] = useState([]);
  const [selectedWalletTransactionIds, setSelectedWalletTransactionIds] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loadingSources, setLoadingSources] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    setId(withdrawal?.id ?? null);
    setAccountId(withdrawal?.account_id ?? "");
    setValue(withdrawal?.value ?? 0);
    setDescription(withdrawal?.description ?? "");
    setReceipt(withdrawal?.receipt ?? null);
    setInitialStatus(withdrawal?.status ?? "")
  }, [withdrawal]);

  const loadAvailableSources = useCallback(async () => {
    try {
      setLoadingSources(true);
      const result = await getWithdrawalAvailableSources();
      const message = getErrorMessage(result);
      if (result.status === 200) {
        setAvailableSources(result.data?.sources || []);
        setAccessDenied(false);
      } else if (message === "Only agents can withdraw") {
        setAccessDenied(true);
      } else {
        toast.error("خطا در دریافت درآمدهای قابل برداشت");
      }
    } finally {
      setLoadingSources(false);
    }
  }, []);

  const loadBankAccounts = useCallback(async () => {
    const result = await getBankAccounts();
    if (result.status === 200) {
      const data = result.data;
      const accounts = Object.values(data).find(item => Array.isArray(item)) || [];
      setBankAccounts(accounts);
    }
  }, []);

  useEffect(() => {
    if (!id) {
      loadAvailableSources();
      loadBankAccounts();
    }
  }, [id, loadAvailableSources, loadBankAccounts]);

  const selectedSources = availableSources.filter(source =>
    source.can_withdraw !== false &&
    selectedWalletTransactionIds.includes(source.wallet_transaction_id)
  );

  const selectedTotal = selectedSources.reduce((sum, source) => sum + Number(source.available_amount || source.amount || 0), 0);
  const selectedBankAccount = bankAccounts.find(account => String(account.id) === String(accountId));
  const canSubmit = selectedWalletTransactionIds.length > 0 && selectedTotal >= MIN_WITHDRAWAL_AMOUNT && Boolean(accountId) && !submitting;

  const toggleSource = (source) => {
    if (source.can_withdraw === false) {
      return;
    }

    const walletTransactionId = source.wallet_transaction_id;

    setSelectedWalletTransactionIds(prev =>
      prev.includes(walletTransactionId)
        ? prev.filter(id => id !== walletTransactionId)
        : [...prev, walletTransactionId]
    );
  };

  const clearSelection = () => {
    setSelectedWalletTransactionIds([]);
  };

  const save = async () => {
    if (!accountId) {
      toast.error("حساب بانکی خود را انتخاب کنید")
      return;
    }
    if (selectedWalletTransactionIds.length === 0) {
      toast.error("حداقل یک درآمد را انتخاب کنید")
      return;
    }
    if (selectedTotal < MIN_WITHDRAWAL_AMOUNT) {
      toast.error(WITHDRAWAL_MESSAGES.minimumAmount)
      return;
    }

    const data = {
      account_id: parseInt(accountId),
      wallet_transaction_ids: selectedWalletTransactionIds,
    };
    if (description?.trim()) {
      data.description = description.trim();
    }

    setSubmitting(true);
    const result = await submitWalletWithdrawal(data)
    setSubmitting(false);

    if (result.status >= 200 && result.status < 300) {
      clearSelection();
      setDescription("");
      setGridSelectedRow(null)
      setLastDataRefresh(Date.now())
      await loadAvailableSources();
      toast.success("درخواست برداشت با موفقیت ثبت شد");
      handleOpen()
      return;
    }

    const message = getErrorMessage(result);
    if (message === "Some selected wallet transactions are invalid or already withdrawn") {
      await loadAvailableSources();
      clearSelection();
      toast.error(WITHDRAWAL_MESSAGES.pendingCustomerPayment);
    } else if (message === "Only agents can withdraw") {
      setAccessDenied(true);
    } else {
      toast.error(getWithdrawalErrorMessage(message));
    }
  };

  const renderSourceType = (source) => {
    if (source.product_type === "service" && source.source_type === "agent_share") {
      return "درآمد خدمت";
    }
    if (source.product_type && source.source_type) {
      return `${Dict(source.product_type)} - ${Dict(source.source_type)}`;
    }
    return source.source_type ? Dict(source.source_type) : "-";
  };

  return (
    <>
      <Dialog open={open} handler={handleOpen} size="xl" animate={{mount: {scale: 1, y: 0}, unmount: {scale: 0.9, y: -100}}}>
        <DialogHeader className="border-b">
          <Typography>{id ? "جزئیات برداشت" : "درخواست برداشت جدید"}</Typography>
        </DialogHeader>
        <DialogBody className="max-h-[78vh] overflow-y-auto">

          {accessDenied ? (
            <Typography color="blue-gray" className="py-8 text-center">
              فقط مشاوران امکان ثبت درخواست برداشت دارند.
            </Typography>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="border-b pb-4">
                <SelectInput
                  className="w-full my-4"
                  label={Dict("bank_accounts")}
                  defaultValue={accountId}
                  refreshSignal={accountId}
                  autoLoadService={getBankAccounts}
                  autoLoadRender={({title, card}) => {
                    return title + " - " + persianNumberFormat(card)
                  }}
                  onChange={setAccountId}
                />
              </div>

              {!id && (
                <>
                  <Textarea
                    label="توضیحات"
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                  />

                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[980px] text-left">
                      <thead>
                      <tr className="border-b border-blue-gray-100">
                        <th className="w-12 p-2 text-center"></th>
                        <th className="p-2 text-center"><Typography className="font-bold" color="blue-gray">مبلغ قابل برداشت</Typography></th>
                        <th className="p-2 text-center"><Typography className="font-bold" color="blue-gray">نوع درآمد</Typography></th>
                        <th className="p-2 text-center"><Typography className="font-bold" color="blue-gray">شناسه خدمت</Typography></th>
                        <th className="p-2 text-center"><Typography className="font-bold" color="blue-gray">رشته/گرایش</Typography></th>
                        <th className="p-2 text-center"><Typography className="font-bold" color="blue-gray">تاریخ تسویه</Typography></th>
                        <th className="p-2 text-center"><Typography className="font-bold" color="blue-gray">تراکنش</Typography></th>
                      </tr>
                      </thead>
                      <tbody className="divide-y divide-blue-gray-50">
                      {loadingSources && (
                        <tr>
                          <td colSpan={7} className="p-8 text-center">
                            <Typography color="blue-gray">در حال دریافت درآمدها...</Typography>
                          </td>
                        </tr>
                      )}
                      {!loadingSources && availableSources.length === 0 && (
                        <tr>
                          <td colSpan={7} className="p-8 text-center">
                            <Typography color="blue-gray">درآمد قابل برداشتی وجود ندارد</Typography>
                          </td>
                        </tr>
                      )}
                      {!loadingSources && availableSources.map((source) => {
                        const walletTransactionId = source.wallet_transaction_id;
                        const checked = selectedWalletTransactionIds.includes(walletTransactionId);
                        const canWithdraw = source.can_withdraw !== false;
                        const serviceId = source.service_id || source.product_id;
                        const sourceDate = source.settled_at || source.created_at;

                        return (
                          <tr
                            key={walletTransactionId}
                            className={
                              canWithdraw
                                ? "cursor-pointer transition-colors hover:bg-blue-gray-50"
                                : "cursor-not-allowed bg-red-50/40"
                            }
                            onClick={() => toggleSource(source)}
                          >
                            <td className="p-2 text-center">
                              <Checkbox
                                checked={checked}
                                disabled={!canWithdraw}
                                onChange={() => toggleSource(source)}
                                onClick={(event) => event.stopPropagation()}
                                crossOrigin={undefined}
                              />
                            </td>
                            <td className="p-2 text-center">
                              <Typography color="blue-gray">
                                {persianNumberFormat(source.available_amount, true)}
                              </Typography>
                              {!canWithdraw && (
                                <Typography color="red" variant="small" className="mt-1 whitespace-normal">
                                  درانتظار پرداخت تکمیلی مشتری. امکان ثبت درخواست برداشت وجه وجود ندارد.
                                </Typography>
                              )}
                            </td>
                            <td className="p-2 text-center">
                              <Typography color="blue-gray">{renderSourceType(source)}</Typography>
                            </td>
                            <td className="p-2 text-center">
                              <Typography color="blue-gray">{serviceId ? persianNumberFormat(serviceId, true) : "-"}</Typography>
                            </td>
                            <td className="p-2 text-center">
                              <Typography color="blue-gray">{[source.field_title, source.branch_title].filter(Boolean).join(" / ") || "-"}</Typography>
                            </td>
                            <td className="p-2 text-center">
                              <Typography color="blue-gray" dir="ltr">
                                <PersianDateTime value={sourceDate}/>
                              </Typography>
                            </td>
                            <td className="p-2 text-center">
                              {source.wallet_product_log_id &&
                                <Typography color="gray" variant="small">
                                  {persianNumberFormat(source.wallet_product_log_id, true)}
                                </Typography>
                              }
                            </td>
                          </tr>
                        );
                      })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {id && (
                <>
                  <div className="mb-4 border-b">
                    <Typography color="blue-gray">
                      {"مبلغ برداشت: " + persianNumberFormat(value, true) + " ریال"}
                    </Typography>
                  </div>

                  {description &&
                    <div className="mb-4 border-b">
                      <Typography>
                        {`توضیحات: ${description}`}
                      </Typography>
                    </div>
                  }

                  <div className="mb-4 border-b">
                    {receipt &&
                      <FileCell fileCode={receipt}/>
                    }
                  </div>

                  {initialStatus &&
                    <Typography color="blue-gray">
                      {"وضعیت: " + (initialStatus === "accepted" ? "انجام شده" : initialStatus === "new" ? "در انتظار بررسی" : initialStatus === "rejected" ? "رد شده" : initialStatus)}
                    </Typography>
                  }
                </>
              )}
            </div>
          )}

        </DialogBody>
        <DialogFooter className="sticky bottom-0 z-10 justify-between gap-4 border-t bg-white">
          {!id && !accessDenied && (
            <div className="grid flex-1 grid-cols-1 gap-2 textRight md:grid-cols-3">
              <Typography color="blue-gray">
                {"تعداد انتخاب شده: " + persianNumberFormat(selectedWalletTransactionIds.length, true)}
              </Typography>
              <Typography color="blue-gray">
                {"جمع انتخاب شده: " + persianNumberFormat(selectedTotal, true) + " ریال"}
              </Typography>
              <Typography color="blue-gray" className="truncate">
                {"حساب مقصد: " + (selectedBankAccount ? `${selectedBankAccount.title} - ${persianNumberFormat(selectedBankAccount.card)}` : "-")}
              </Typography>
            </div>
          )}
          <div className="flex shrink-0 gap-2">
            <Button variant="text" color="gray" onClick={handleOpen}>
              <span>بستن</span>
            </Button>
            {!id && !accessDenied &&
              <Button variant="gradient" color="gray" onClick={save} disabled={!canSubmit}>
                <span>{submitting ? "در حال ثبت..." : "ثبت درخواست"}</span>
              </Button>
            }
          </div>
        </DialogFooter>
      </Dialog>
    </>
  );
}
